import React from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { InfoItem } from 'components'
import Icon, { InfoIcon } from 'components/Icon'
import ConfirmModal from 'components/ConfirmModal'
import NoImage from 'assets/no-image.jpg'
import { useDeleteItem, useEventSlug } from 'hooks'
import { _, colors } from 'utils'
import { addModal } from 'features/modals/modalSlice'
import { getEventData } from 'features/events/eventSlice'
import { BoothCategoryType } from 'generated/graphql'
import { Booth } from '../BoothList'
import InfoPopover from './InfoPopover'
import CreateUpdateBooth from '../CreateUpdateBooth'
import { deleteBooth } from '../graphql/mutation'

const S = {
    InfoItem: styled(InfoItem)<{ disableSort: boolean }>`
        cursor: ${({ disableSort }) => (disableSort ? 'auto' : 'move')};
        overflow: visible;
    `,
    InfoIconWrapper: styled.div`
        position: relative;
        height: 100%;
        display: flex;
        align-items: center;
        padding-left: 32px;
        & div.popover-menu{
            position: absolute;
            bottom: 4px;
            right: 0;
            z-index: 1;
            transform: translateY(100%);
            display: none;
            background: white;
            box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
            border-radius: 2px;
        }
        &:hover div.popover-menu {
            display: block;
        }
    `,
    PopoverItem: styled.div`
        min-width: 220px;
        height: 32px;
        padding: 8px 11px;
        display: flex;
        align-items: center;
        color: ${colors.icon};
        font-weight: 300;
        font-size: 14px;
        white-space: nowrap;
        border-bottom: 1px solid rgba(176, 190, 197, 0.3);
        & > span {
            margin-right: 8px;
            margin-bottom: 3px;
        }
        & > a {
            font-weight: 500;
            color: ${colors.grayTitle};
        }
    `,
}

interface Props {
    booth: Booth
    disableSort?: boolean
    callGetBooths?: () => Promise<void>
    categories?: BoothCategoryType[]
}

const BoothRow = ({ booth, disableSort, callGetBooths, categories }: Props) => {
    const eventSlug = useEventSlug()
    const dispatch = useDispatch()
    const deleteFn = async (id) => {
        await deleteBooth({ id })
        dispatch(getEventData(eventSlug))
    }
    const { showDeleteConfirmModal, handleDelete } = useDeleteItem(
        deleteFn, callGetBooths
    )

    const showUpdateModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <CreateUpdateBooth
                    action="update"
                    close={close}
                    callGetBooths={callGetBooths}
                    booth={booth}
                    categories={categories}
                />
            ),
            header: _('booths.update_booth.modal_header'),
            scrollable: true,
        }))
    }
    const showDeleteLinkedBoothModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <ConfirmModal
                    close={close}
                    title={_('delete_confirm.title')}
                    confirmDescription={_('booths.delete_confirm.booth_linked_spaces.description')}
                    confirmText={_('delete_confirm.confirm_button')}
                    onConfirm={() => handleDelete(booth.id)}
                    variantConfirm="primary"
                />
            ),
            shouldNotWrap: true,
        }))
    }

    const isBoothLinkedSpaces = booth.parent.linkedFrom.map((e) => e.id).length > 0
    const handleDeleteConfirm = isBoothLinkedSpaces
        ? showDeleteLinkedBoothModal
        : () => showDeleteConfirmModal(booth.id)

    return (
        <S.InfoItem
            image={booth?.companyLogo || NoImage}
            name={booth?.parent?.name}
            description={booth?.subtitle}
            disableSort={disableSort}
            squareImage
        >
            <S.InfoIconWrapper>
                <InfoIcon size={18} />
                <InfoPopover booth={booth} />
            </S.InfoIconWrapper>
            <Link to={`/${eventSlug}/booth/${booth?.parent?.slug}`}>
                <Icon
                    icon="edit"
                    size={18}
                    onClick={() => null}
                />
            </Link>
            <Icon
                icon="cog"
                size={18}
                onClick={showUpdateModal}
            />
            <Icon
                icon="trash"
                size={18}
                onClick={handleDeleteConfirm}
            />
        </S.InfoItem>
    )
}

export default BoothRow
